/**
 * =============================================================================
 * Data Table Component
 * =============================================================================
 *
 * Reusable table component for displaying data lists.
 *
 * Features:
 * - Column configuration
 * - Loading state
 * - Empty state
 * - Row click handler
 * - Custom cell rendering
 * - Sortable columns
 * - Text search filtering
 */

import React, { useState, useMemo } from 'react'

// =============================================================================
// Types
// =============================================================================

/**
 * Column definition for the data table.
 */
export interface Column<T> {
  /** Unique key for the column */
  key: string
  /** Column header text */
  header: string
  /** Property name to display (or custom render function) */
  accessor?: keyof T
  /** Custom render function for cell content */
  render?: (item: T) => React.ReactNode
  /** Column width class */
  width?: string
  /** Enable sorting for this column */
  sortable?: boolean
  /** Custom sort function (for complex values) */
  sortFn?: (a: T, b: T) => number
  /** Make this column searchable */
  searchable?: boolean
  /** Custom search function */
  searchFn?: (item: T, query: string) => boolean
}

type SortDirection = 'asc' | 'desc' | null

interface SortConfig {
  key: string
  direction: SortDirection
}

interface DataTableProps<T> {
  /** Column definitions */
  columns: Column<T>[]
  /** Data to display */
  data: T[]
  /** Loading state */
  loading?: boolean
  /** Row key accessor */
  getRowKey: (item: T) => string
  /** Row click handler */
  onRowClick?: (item: T) => void
  /** Empty state message */
  emptyMessage?: string
  /** Enable search functionality */
  searchable?: boolean
  /** Search placeholder */
  searchPlaceholder?: string
}

// =============================================================================
// Sort Icon Component
// =============================================================================

interface SortIconProps {
  direction: SortDirection
  active: boolean
}

const SortIcon: React.FC<SortIconProps> = ({ direction, active }) => {
  return (
    <span className={`ml-1 inline-flex flex-col ${active ? 'text-primary-600' : 'text-gray-400'}`}>
      <svg
        className={`w-3 h-3 -mb-1 ${direction === 'asc' && active ? 'text-primary-600' : ''}`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M5 10l5-5 5 5H5z" />
      </svg>
      <svg
        className={`w-3 h-3 ${direction === 'desc' && active ? 'text-primary-600' : ''}`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M5 10l5 5 5-5H5z" />
      </svg>
    </span>
  )
}

// =============================================================================
// Component
// =============================================================================

/**
 * Generic data table component with sorting and filtering.
 *
 * @example
 * const columns: Column<User>[] = [
 *   { key: 'name', header: 'Name', accessor: 'name', sortable: true, searchable: true },
 *   { key: 'email', header: 'Email', accessor: 'email', sortable: true },
 *   { key: 'status', header: 'Status', render: (user) => <StatusBadge status={user.status} /> },
 * ]
 *
 * <DataTable
 *   columns={columns}
 *   data={users}
 *   getRowKey={(user) => user.id}
 *   onRowClick={(user) => navigate(`/users/${user.id}`)}
 *   searchable
 * />
 */
function DataTable<T>({
  columns,
  data,
  loading = false,
  getRowKey,
  onRowClick,
  emptyMessage = 'No data available',
  searchable = false,
  searchPlaceholder = 'Search...',
}: DataTableProps<T>) {
  // Sort state
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: '', direction: null })

  // Search state
  const [searchQuery, setSearchQuery] = useState('')

  // Handle column header click for sorting
  const handleSort = (column: Column<T>) => {
    if (!column.sortable) return

    let direction: SortDirection = 'asc'
    if (sortConfig.key === column.key) {
      if (sortConfig.direction === 'asc') {
        direction = 'desc'
      } else if (sortConfig.direction === 'desc') {
        direction = null
      }
    }

    setSortConfig({ key: column.key, direction })
  }

  // Filter and sort data
  const processedData = useMemo(() => {
    let result = [...data]

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      result = result.filter((item) => {
        return columns.some((column) => {
          // Use custom search function if provided
          if (column.searchFn) {
            return column.searchFn(item, query)
          }
          // Only search in searchable columns or if column has accessor
          if (column.searchable !== false && column.accessor) {
            const value = item[column.accessor]
            if (value != null) {
              return String(value).toLowerCase().includes(query)
            }
          }
          return false
        })
      })
    }

    // Apply sorting
    if (sortConfig.key && sortConfig.direction) {
      const column = columns.find((c) => c.key === sortConfig.key)
      if (column) {
        result.sort((a, b) => {
          // Use custom sort function if provided
          if (column.sortFn) {
            const sortResult = column.sortFn(a, b)
            return sortConfig.direction === 'asc' ? sortResult : -sortResult
          }

          // Default sorting by accessor
          if (column.accessor) {
            const aValue = a[column.accessor]
            const bValue = b[column.accessor]

            // Handle null/undefined
            if (aValue == null && bValue == null) return 0
            if (aValue == null) return sortConfig.direction === 'asc' ? 1 : -1
            if (bValue == null) return sortConfig.direction === 'asc' ? -1 : 1

            // Handle dates
            if (aValue instanceof Date && bValue instanceof Date) {
              return sortConfig.direction === 'asc'
                ? aValue.getTime() - bValue.getTime()
                : bValue.getTime() - aValue.getTime()
            }

            // Handle strings (case-insensitive)
            if (typeof aValue === 'string' && typeof bValue === 'string') {
              const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase())
              return sortConfig.direction === 'asc' ? comparison : -comparison
            }

            // Handle numbers
            if (typeof aValue === 'number' && typeof bValue === 'number') {
              return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue
            }

            // Fallback to string comparison
            const aStr = String(aValue)
            const bStr = String(bValue)
            const comparison = aStr.localeCompare(bStr)
            return sortConfig.direction === 'asc' ? comparison : -comparison
          }

          return 0
        })
      }
    }

    return result
  }, [data, columns, sortConfig, searchQuery])

  // Loading state
  if (loading) {
    return (
      <div className="table-container">
        <div className="p-8 text-center text-gray-500">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary-600 border-t-transparent mb-2" />
          <p>Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      {searchable && (
        <div className="relative">
          <input
            type="text"
            className="input pl-10 w-full max-w-md"
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <svg
            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      )}

      {/* Results count when searching */}
      {searchQuery && (
        <p className="text-sm text-gray-500">
          Found {processedData.length} of {data.length} records
        </p>
      )}

      {/* Empty state */}
      {processedData.length === 0 ? (
        <div className="table-container">
          <div className="p-8 text-center text-gray-500">
            <p>{searchQuery ? 'No matching records found' : emptyMessage}</p>
          </div>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            {/* Header */}
            <thead>
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className={`${column.width || ''} ${column.sortable ? 'cursor-pointer select-none hover:bg-gray-100' : ''}`}
                    onClick={() => handleSort(column)}
                  >
                    <div className="flex items-center">
                      {column.header}
                      {column.sortable && (
                        <SortIcon
                          direction={sortConfig.key === column.key ? sortConfig.direction : null}
                          active={sortConfig.key === column.key}
                        />
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            {/* Body */}
            <tbody className="bg-white divide-y divide-gray-200">
              {processedData.map((item) => (
                <tr
                  key={getRowKey(item)}
                  onClick={() => onRowClick?.(item)}
                  className={onRowClick ? 'cursor-pointer' : ''}
                >
                  {columns.map((column) => (
                    <td key={column.key}>
                      {column.render
                        ? column.render(item)
                        : column.accessor
                        ? String(item[column.accessor] ?? '-')
                        : '-'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default DataTable
